<template>
  <div>
    <div
      :class="[
        'relative rounded-2xl overflow-hidden cursor-pointer group',
        aspectClasses[aspect]
      ]"
      @click="showModal = true"
      role="button"
      tabindex="0"
      @keydown.enter="showModal = true"
      @keydown.space="showModal = true"
    >
      <!-- Real image -->
      <img
        v-if="imageExists"
        :src="imageSrc"
        :alt="label"
        class="w-full h-full object-cover transition-transform group-hover:scale-105"
        @error="onImageError"
      />
      
      <!-- Fallback placeholder -->
      <div
        v-else
        :class="[
          'w-full h-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-500'
        ]"
      >
        <div class="text-center">
          <div class="w-12 h-12 mx-auto mb-2 opacity-50">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <p class="text-sm font-medium">{{ label }}</p>
        </div>
      </div>
      
      <!-- Zoom icon overlay -->
      <div v-if="imageExists" class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
        <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
        </svg>
      </div>
    </div>

    <!-- Modal -->
    <Teleport to="body">
      <div
        v-if="showModal"
        class="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-2"
        @click="showModal = false"
      >
        <!-- Close button -->
        <button
          @click="showModal = false"
          class="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-20 bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75"
          aria-label="Close modal"
        >
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <!-- Image container - maximize available space -->
        <div class="w-full h-full flex flex-col items-center justify-center" @click.stop>
          <img
            :src="imageSrc"
            :alt="label"
            class="max-w-[95vw] max-h-[90vh] w-auto h-auto object-contain rounded-lg"
          />
        </div>
        
        <!-- Label at bottom -->
        <div class="absolute bottom-4 left-4 right-4 text-white text-center text-sm bg-black bg-opacity-50 rounded px-4 py-2">
          {{ label }}
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

interface Props {
  imageSrc: string
  label: string
  aspect?: '16/9' | '4/3' | '1/1'
}

const props = withDefaults(defineProps<Props>(), {
  aspect: '16/9'
})

const imageExists = ref(false)
const showModal = ref(false)

const aspectClasses = {
  '16/9': 'aspect-video',
  '4/3': 'aspect-[4/3]',
  '1/1': 'aspect-square'
}

const checkImageExists = async () => {
  try {
    const response = await fetch(props.imageSrc, { method: 'HEAD' })
    imageExists.value = response.ok
  } catch {
    imageExists.value = false
  }
}

const onImageError = () => {
  imageExists.value = false
}

onMounted(() => {
  checkImageExists()
})
</script>